// S4 (v0.15.0) — User detail: profile + role assignments + suspend.
import { useState } from "react";
import { useParams, Link } from "react-router-dom";

import { api } from "@/api/client";
import {
  useAssignUserRole,
  useReactivateUser,
  useRevokeUserRole,
  useRoles,
  useSuspendUser,
  useUserRoles,
} from "@/api/users";
import { useEntities } from "@/api/entities";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { useFormatTimestamp } from "@/utils/datetime";
import { useQuery } from "@tanstack/react-query";

interface UserDetail {
  id: string;
  email: string;
  full_name: string | null;
  role: string;
  is_active: boolean;
  is_mfa_enabled: boolean;
  last_login_at: string | null;
}

export default function UserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const fmt = useFormatTimestamp();
  const { data: user } = useQuery<UserDetail>({
    queryKey: ["users", id, "detail"],
    queryFn: async () => {
      const { data } = await api.get<UserDetail>(`/api/v1/users/${id}`);
      return data;
    },
    enabled: Boolean(id),
  });
  const { data: roleRows = [] } = useUserRoles(id);
  const { data: roles = [] } = useRoles();
  const { data: roots = [] } = useEntities();
  const assign = useAssignUserRole(id ?? "");
  const revoke = useRevokeUserRole(id ?? "");
  const suspend = useSuspendUser();
  const reactivate = useReactivateUser();

  const [newRole, setNewRole] = useState("");
  const [newEntity, setNewEntity] = useState("");
  const [newScope, setNewScope] = useState<"subtree" | "exact">("subtree");
  const [addError, setAddError] = useState<string | null>(null);

  if (!user) return <p className="text-sm">Loading…</p>;

  const roleNameById = new Map(roles.map((r) => [r.id, r.name]));

  async function handleAdd() {
    setAddError(null);
    if (!newRole || !newEntity) {
      setAddError("Pick a role and an entity");
      return;
    }
    // A8/AU-165 — granting a role can escalate privileges (incl. to admin). There
    // is no base-role edit control, so role assignment IS the privilege-change
    // surface; confirm before mutating, matching the Suspend guard.
    if (
      !window.confirm(
        `Grant role "${roleNameById.get(newRole) ?? "the selected role"}" to ` +
          `${user?.email ?? "this user"}? This changes their access.`,
      )
    )
      return;
    try {
      await assign.mutateAsync({
        role_id: newRole,
        entity_id: newEntity,
        scope: newScope,
      });
      setNewRole("");
      setNewEntity("");
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })
        .response?.data?.detail;
      setAddError(typeof detail === "string" ? detail : "Failed");
    }
  }

  async function handleSuspendToggle() {
    if (!id || !user) return;
    const verb = user.is_active ? "Suspend" : "Reactivate";
    if (!confirm(`${verb} ${user.email}? Sessions will be revoked.`)) return;
    if (user.is_active) await suspend.mutateAsync(id);
    else await reactivate.mutateAsync(id);
    // refetch detail
    window.location.reload();
  }

  return (
    <div className="mx-auto max-w-5xl space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {user.full_name || user.email}
          </h1>
          <p className="text-sm text-muted-foreground">{user.email}</p>
        </div>
        <Button asChild variant="outline">
          <Link to="/users">Back</Link>
        </Button>
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardContent className="space-y-3 p-4">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              Profile
            </h2>
            <dl className="grid grid-cols-[140px_1fr] gap-y-2 text-sm">
              <dt className="text-muted-foreground">Status</dt>
              <dd>
                <Badge variant={user.is_active ? "online" : "error"}>
                  {user.is_active ? "Active" : "Suspended"}
                </Badge>
              </dd>
              <dt className="text-muted-foreground">Legacy Role</dt>
              <dd>{user.role}</dd>
              <dt className="text-muted-foreground">MFA</dt>
              <dd>
                <Badge variant={user.is_mfa_enabled ? "online" : "offline"}>
                  {user.is_mfa_enabled ? "Enabled" : "Not enrolled"}
                </Badge>
              </dd>
              <dt className="text-muted-foreground">Last Login</dt>
              <dd>
                {user.last_login_at
                  ? fmt(user.last_login_at, "relative")
                  : "Never"}
              </dd>
            </dl>
            <Button
              variant={user.is_active ? "destructive" : "default"}
              onClick={handleSuspendToggle}
            >
              {user.is_active ? "Suspend" : "Reactivate"}
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="space-y-3 p-4">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              Role Assignments
            </h2>
            {roleRows.length === 0 ? (
              <p className="text-sm text-muted-foreground">No role assignments.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="text-left">Role</th>
                    <th className="text-left">Entity ID</th>
                    <th className="text-left">Scope</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {roleRows.map((r) => (
                    <tr key={r.id} className="border-t border-border">
                      <td className="py-1">
                        {roleNameById.get(r.role_id) ?? r.role_id.slice(0, 8)}
                      </td>
                      <td className="py-1 font-mono text-xs">
                        {r.entity_id.slice(0, 8)}…
                      </td>
                      <td className="py-1">
                        <Badge variant="offline">{r.scope}</Badge>
                      </td>
                      <td className="py-1 text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            // A8/AU-165 — confirm before removing access.
                            if (
                              window.confirm(
                                `Remove role "${roleNameById.get(r.role_id) ?? "this role"}" ` +
                                  `from ${user.email}?`,
                              )
                            )
                              revoke.mutate(r.id);
                          }}
                        >
                          Remove
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <div className="space-y-2 border-t border-border pt-3">
              <p className="text-xs font-semibold uppercase text-muted-foreground">
                Assign Role
              </p>
              <div>
                <Label>Role</Label>
                <select
                  value={newRole}
                  onChange={(e) => setNewRole(e.target.value)}
                  className="h-9 w-full rounded border border-border bg-background px-2 text-sm"
                >
                  <option value="">Choose…</option>
                  {roles.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Entity</Label>
                <select
                  value={newEntity}
                  onChange={(e) => setNewEntity(e.target.value)}
                  className="h-9 w-full rounded border border-border bg-background px-2 text-sm"
                >
                  <option value="">Choose…</option>
                  {roots.map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-x-3 text-sm">
                <label className="inline-flex items-center gap-1">
                  <input
                    type="radio"
                    checked={newScope === "subtree"}
                    onChange={() => setNewScope("subtree")}
                  />
                  Subtree
                </label>
                <label className="inline-flex items-center gap-1">
                  <input
                    type="radio"
                    checked={newScope === "exact"}
                    onChange={() => setNewScope("exact")}
                  />
                  Exact
                </label>
              </div>
              {addError && (
                <p className="rounded bg-red-500/10 px-2 py-1 text-xs text-red-600">
                  {addError}
                </p>
              )}
              <Button onClick={handleAdd} disabled={assign.isPending}>
                Add Role
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
